import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageSkeleton } from "@/components/PageSkeleton";
import { toast } from "sonner";
import { Settings, MapPin } from "lucide-react";

export default function AttendanceSettings() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/attendance/settings").then((r) => setForm(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/attendance/settings", {
        check_in_time: form.check_in_time,
        tolerance_minutes: parseInt(form.tolerance_minutes) || 0,
        checkout_mon_fri: form.checkout_mon_fri,
        checkout_sat: form.checkout_sat,
        sunday_off: !!form.sunday_off,
        admin_leave_quota: parseInt(form.admin_leave_quota) || 0,
        overtime_scope: form.overtime_scope || "period",
        gps_enabled: !!form.gps_enabled,
        office_lat: form.office_lat === "" || form.office_lat == null ? null : parseFloat(form.office_lat),
        office_lng: form.office_lng === "" || form.office_lng == null ? null : parseFloat(form.office_lng),
        office_radius_m: parseInt(form.office_radius_m) || 100,
      });
      toast.success("Pengaturan absensi disimpan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error("GPS tidak tersedia di perangkat ini"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => { setForm({ ...form, office_lat: p.coords.latitude, office_lng: p.coords.longitude }); toast.success("Lokasi kantor terisi dari GPS perangkat ini"); },
      () => toast.error("Lokasi tidak terbaca — aktifkan izin lokasi"),
      { enableHighAccuracy: true, timeout: 10000 });
  };

  if (!form) return <PageSkeleton stats={0} rows={6} />;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="attendance-settings-page">
      <div>
        <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight flex items-center gap-2"><Settings className="h-5 w-5 text-[#C9A227]" /> Pengaturan Absensi</h2>
        <p className="text-xs sm:text-sm text-muted-foreground">Atur jam kerja, toleransi keterlambatan, dan jatah libur Admin.</p>
      </div>
      <Card className="p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Jam Masuk (WIB)</Label><Input type="time" value={form.check_in_time} onChange={(e) => setForm({ ...form, check_in_time: e.target.value })} data-testid="set-check-in-time" /></div>
          <div className="space-y-1.5"><Label>Toleransi Terlambat (menit)</Label><Input type="number" value={form.tolerance_minutes} onChange={(e) => setForm({ ...form, tolerance_minutes: e.target.value })} data-testid="set-tolerance" /></div>
          <div className="space-y-1.5"><Label>Jam Pulang Senin–Jumat</Label><Input type="time" value={form.checkout_mon_fri} onChange={(e) => setForm({ ...form, checkout_mon_fri: e.target.value })} data-testid="set-checkout-weekday" /></div>
          <div className="space-y-1.5"><Label>Jam Pulang Sabtu</Label><Input type="time" value={form.checkout_sat} onChange={(e) => setForm({ ...form, checkout_sat: e.target.value })} data-testid="set-checkout-sat" /></div>
          <div className="space-y-1.5"><Label>Jatah Libur Admin / Bulan</Label><Input type="number" value={form.admin_leave_quota} onChange={(e) => setForm({ ...form, admin_leave_quota: e.target.value })} data-testid="set-admin-leave" /></div>
          <div className="space-y-1.5">
            <Label>Penggantian Kekurangan (Lembur)</Label>
            <Select value={form.overtime_scope || "period"} onValueChange={(v) => setForm({ ...form, overtime_scope: v })}>
              <SelectTrigger data-testid="set-overtime-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="period">Lintas hari (total periode)</SelectItem>
                <SelectItem value="same_day">Hanya hari yang sama</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <div><p className="text-sm font-medium">Minggu Libur</p><p className="text-[11px] text-muted-foreground">Jika aktif, hari Minggu tidak dihitung jam pulang karyawan.</p></div>
          <Switch checked={!!form.sunday_off} onCheckedChange={(v) => setForm({ ...form, sunday_off: v })} data-testid="set-sunday-off" />
        </div>
        <div className="flex justify-end">
          <Button variant="gold" disabled={busy} onClick={save} data-testid="set-save-btn">{busy ? "Menyimpan..." : "Simpan Pengaturan"}</Button>
        </div>
      </Card>

      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading text-sm font-semibold">Validasi Lokasi (GPS / Geofence)</h3>
            <p className="text-[11px] text-muted-foreground">Bila aktif, karyawan hanya bisa absen di radius kantor dan admin di radius rumah terdaftarnya. Absen di luar area tetap tercatat dengan peringatan & butuh persetujuan Owner.</p>
          </div>
          <Switch checked={!!form.gps_enabled} onCheckedChange={(v) => setForm({ ...form, gps_enabled: v })} data-testid="set-gps-enabled" />
        </div>
        {form.gps_enabled && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Latitude Kantor</Label><Input type="number" step="any" value={form.office_lat ?? ""} onChange={(e) => setForm({ ...form, office_lat: e.target.value })} data-testid="set-office-lat" placeholder="-6.2" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Longitude Kantor</Label><Input type="number" step="any" value={form.office_lng ?? ""} onChange={(e) => setForm({ ...form, office_lng: e.target.value })} data-testid="set-office-lng" placeholder="106.8" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Radius (meter)</Label><Input type="number" value={form.office_radius_m} onChange={(e) => setForm({ ...form, office_radius_m: e.target.value })} data-testid="set-office-radius" /></div>
            <div className="sm:col-span-3">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={useCurrentLocation} data-testid="set-use-gps"><MapPin className="h-3.5 w-3.5" /> Pakai Lokasi Perangkat Ini sebagai Kantor</Button>
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <Button variant="gold" disabled={busy} onClick={save} data-testid="set-save-gps-btn">{busy ? "Menyimpan..." : "Simpan Pengaturan"}</Button>
        </div>
      </Card>
    </div>
  );
}
